import { useRef, useState } from "react";
import type { ProUpsellVariant } from "./components/ProUpsellCard";
import { hasShownEmailPromptRecently, markEmailPromptShown } from "./emailPromptStorage";
import { hasClickedEditorPromo, hasSeenEditorPromoToday, markEditorPromoClicked, markEditorPromoShown } from "./editorPromoStorage";
import {
  hasClickedProInterested,
  hasSeenProUpsellToday,
  markProInterested,
  markProUpsellShown,
} from "./proUpsellStorage";

interface UseDownloadPromptsArgs {
  apiUrl: string;
  token: string;
  // The library entry the just-downloaded file belongs to. Everything here
  // is a no-op without one -- both prompts need something to email or
  // upsell against -- so a page that has no asset for a given download
  // (still loading, or a flow that never got a token) just never shows them.
  assetId: string | null | undefined;
  emailCaptureEnabled: boolean;
  // "control" (the flag's off/holdout state), "price_19", or "price_29" --
  // see the flag read on CreatePageProps.proUpsellVariant for why this is
  // always a hydrated prop, never read client-side via posthog.getFeatureFlag.
  proUpsellFlag: string;
  // Whether a download may be followed by the card pointing at /editor. Off
  // unless a page opts in: only /create does today.
  editorPromoEnabled?: boolean;
}

/**
 * The post-download prompts -- "email me this", the Pro fake-door, and on
 * /create the editor promo --
 * factored out of CreatePage so /convert and /replay show them too, through
 * the exact same plumbing rather than a copy of it. See notifyDownload's own
 * comment for what "same plumbing" means operationally: every call site
 * fires its own download event first, entirely unchanged, then calls this.
 */
export function useDownloadPrompts({
  apiUrl,
  token,
  assetId,
  emailCaptureEnabled,
  proUpsellFlag,
  editorPromoEnabled = false,
}: UseDownloadPromptsArgs) {
  const [emailPrompt, setEmailPrompt] = useState<{ format: string; prefilledEmail?: string; requested?: boolean } | null>(null);
  // Whether notifyDownload has finished deciding whether to show emailPrompt
  // for the most recent download. Distinct from emailPrompt itself, which
  // stays null in two different situations the Pro card needs to tell apart:
  // "no email prompt is coming" and "one might still show up." Without this,
  // the Pro card would flash on screen during the gap between the download
  // starting and the prefill fetch resolving, then vanish the instant
  // emailPrompt becomes non-null.
  const [emailPromptSettled, setEmailPromptSettled] = useState(true);
  const [proUpsellVariant, setProUpsellVariant] = useState<ProUpsellVariant | null>(null);
  const [editorPromo, setEditorPromo] = useState(false);
  // Whether the modal has opened on its own within the last six hours (see
  // emailPromptStorage). Read once on mount, then flipped here the moment it
  // opens, so the button below the downloads appears without a reload.
  const [emailPromptCoolingDown, setEmailPromptCoolingDown] = useState(hasShownEmailPromptRecently);
  // The button pre-checks whatever was downloaded last, same as the
  // automatic modal pre-checks the format that was just clicked.
  const lastDownloadedFormatRef = useRef<string | null>(null);

  const canEmailDesign = Boolean(emailCaptureEnabled && assetId && token);

  const fetchPrefilledEmail = (): Promise<string | undefined> =>
    fetch(`${apiUrl}/api/link/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (!res.ok) return undefined;
        const data = await res.json();
        return typeof data.email === "string" ? data.email : undefined;
      })
      .catch((err) => {
        console.warn("Failed to prefill email", err);
        return undefined;
      });

  const notifyDownload = async (format: string) => {
    lastDownloadedFormatRef.current = format;
    setEmailPromptSettled(false);

    // Pro upsell fake-door test. Governed ONLY by proUpsellFlag -- never
    // gated on emailCaptureEnabled/assetId, and never suppresses the
    // design-email capture below; the two coexist. "control" renders nothing
    // and fires no pro_* events. Two independent caps: the daily one below,
    // and a permanent one once someone has already clicked "I'm interested"
    // -- see hasClickedProInterested's own comment for why that one never
    // expires.
    if (
      token &&
      (proUpsellFlag === "price_19" || proUpsellFlag === "price_29") &&
      !hasSeenProUpsellToday(token) &&
      !hasClickedProInterested(token)
    ) {
      markProUpsellShown(token);
      setProUpsellVariant(proUpsellFlag);
    } else if (
      // The editor promo, capped the same two ways. Only on a download that
      // didn't just earn the Pro card: two sales pitches back to back for
      // one click is one too many. Its cap isn't marked in that case, so a
      // later download can still show it.
      token &&
      editorPromoEnabled &&
      !hasSeenEditorPromoToday(token) &&
      !hasClickedEditorPromo(token)
    ) {
      markEditorPromoShown(token);
      setEditorPromo(true);
    }

    // Offered only when we know which library entry to send. Without an id
    // there is nothing to email. Re-read storage rather than trusting
    // emailPromptCoolingDown alone: another tab may have shown it since.
    if (canEmailDesign && !emailPromptCoolingDown && !hasShownEmailPromptRecently()) {
      markEmailPromptShown();
      setEmailPromptCoolingDown(true);
      const prefilledEmail = await fetchPrefilledEmail();
      setEmailPrompt({ format, prefilledEmail });
    } else if (canEmailDesign) {
      setEmailPromptCoolingDown(true);
    }
    setEmailPromptSettled(true);
  };

  return {
    emailPrompt,
    emailPromptSettled,
    proUpsellVariant,
    editorPromo,
    notifyDownload,
    // The "Email me this design" button under the downloads, offered only
    // while the automatic modal is capped. Opening it by hand doesn't touch
    // the cap: it was asked for, not an interruption.
    showEmailDesignButton: canEmailDesign && emailPromptCoolingDown,
    openEmailPrompt: async (fallbackFormat: string) => {
      const format = lastDownloadedFormatRef.current ?? fallbackFormat;
      const prefilledEmail = await fetchPrefilledEmail();
      setEmailPrompt({ format, prefilledEmail, requested: true });
    },
    closeEmailPrompt: () => setEmailPrompt(null),
    closeProUpsell: () => setProUpsellVariant(null),
    // Passed to ProUpsellCard's onInterested -- called the moment someone
    // clicks "I'm interested," not on close, so the permanent cap applies
    // even if they abandon the flow before reaching "confirmed."
    markProUpsellInterested: () => markProInterested(token),
    closeEditorPromo: () => setEditorPromo(false),
    // Passed to EditorPromoCard's onClicked: once they've gone to the
    // editor, it's never shown to them again.
    markEditorPromoClicked: () => markEditorPromoClicked(token),
  };
}
